import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { apiResponse, createApiErrorResponse, ResponseObj, wrap } from './services/base';

export type ShouldRetryFunction = (xhr: XMLHttpRequest) => boolean;

const defaultShouldRetryFunction: ShouldRetryFunction = (xhr): boolean => {
  switch (xhr.status) {
    case 0:
    case 409:
    case 502:
    case 503:
      return true;
    default:
      return false;
  }
};

type PromiseHandler = (value?: AxiosResponse | AxiosError) => void;

export function ajaxRetry<SuccessResponseType, FailureResponseType = SuccessResponseType>(
  settings: AxiosRequestConfig,
  shouldRetry: ShouldRetryFunction = defaultShouldRetryFunction,
  maxTries = 3,
  interval = 3000
): Promise<ResponseObj<SuccessResponseType> | ResponseObj<FailureResponseType>> {
  let retryCount = 0;
  const isIdempotent = settings.method === undefined || settings.method === 'GET';

  let idempotencyKeyHeaderPresent = false;

  if (settings.headers) {
    idempotencyKeyHeaderPresent = settings.headers['Idempotency-Key'] as boolean;
  }

  const tryAjax = (oldResolver?: PromiseHandler, oldRejector?: PromiseHandler) => {
    return new Promise((newResolver, newRejector) => {
      const resolve = oldResolver || newResolver;
      const reject = oldRejector || newRejector;

      axios
        .request(settings)
        .then((response: AxiosResponse<SuccessResponseType>) => {
          resolve(response);
        })
        .catch((response: AxiosError<FailureResponseType>) => {
          retryCount += 1;

          if (
            retryCount < maxTries &&
            (isIdempotent || idempotencyKeyHeaderPresent) &&
            shouldRetry(response.request)
          ) {
            setTimeout(() => {
              tryAjax(resolve, reject);
            }, interval);
          } else {
            reject(response);
          }
        });
    });
  };

  return new Promise((resolve) => {
    tryAjax()
      .then((response) => {
        wrap(() => {
          resolve(apiResponse(response as AxiosResponse<SuccessResponseType>));
        });
      })
      .catch((response: AxiosError<FailureResponseType>) => {
        resolve(createApiErrorResponse(response));
      });
  });
}
